import {
	launcherColors,
	parseShortcut,
	FEATURES,
	isRtl,
	type Lang,
	type ToolbarConfig,
} from './config';
import { h } from './dom';
import type { Labels } from './i18n';
import {
	FONT_SCALES,
	LETTER_SPACINGS,
	LINE_HEIGHTS,
	WORD_SPACINGS,
	type Prefs,
} from './prefs';

export const HOST_TAG = 'nagish-marketbiz-toolbar';

export interface UiOptions {
	cfg: ToolbarConfig;
	lang: Lang;
	labels: Labels;
	css: string;
	onChange: ( patch: Partial< Prefs > ) => void;
	onReset: () => void;
	onRead: () => void;
	onStop: () => void;
	voiceAvailable: () => boolean;
}

export interface Ui {
	host: HTMLElement;
	sync: ( p: Prefs ) => void;
	refreshVoices: () => void;
	toggle: () => void;
	isOpen: () => boolean;
}

const ICON =
	'<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 2a2.2 2.2 0 1 1 0 4.4A2.2 2.2 0 0 1 12 2zm8 6.2-5.4.7v3.4l1.8 7.4-2 .5L12.8 14h-1.6l-1.6 6.2-2-.5 1.8-7.4V8.9L4 8.2V6.2l8 1.1 8-1.1z"/></svg>';

let uid = 0;

export const createUi = ( o: UiOptions ): Ui => {
	const { cfg, labels: L } = o;
	const on = ( id: ( typeof FEATURES )[ number ] ) => cfg.features[ id ];
	const syncers: Array< ( p: Prefs ) => void > = [];
	let current: Prefs;
	let open = false;
	let pointerDriven = false;
	const id = ( name: string ) => `nz-${ name }-${ ++uid }`;

	const host = document.createElement( HOST_TAG );
	const pageDir =
		getComputedStyle( document.documentElement ).direction === 'rtl'
			? 'rtl'
			: 'ltr';
	host.setAttribute( 'dir', pageDir );
	host.dataset.pos = cfg.position;
	const colors = launcherColors( cfg.color );
	host.style.setProperty( '--nz-bg', colors.bg );
	host.style.setProperty( '--nz-fg', colors.fg );
	const shadow = host.attachShadow( { mode: 'open' } );

	const toggleBtn = ( label: string, key: keyof Prefs, enabled = true ) => {
		const b = h(
			'button',
			{ type: 'button', class: 'opt', 'aria-pressed': 'false' },
			h( 'span', {}, label )
		);
		b.addEventListener(
			'click',
			() => enabled && o.onChange( { [ key ]: ! current[ key ] } )
		);
		syncers.push( ( p ) =>
			b.setAttribute( 'aria-pressed', String( Boolean( p[ key ] ) ) )
		);
		return b;
	};

	const stepper = (
		name: string,
		key: 'fontSize' | 'lineHeight' | 'letterSpacing' | 'wordSpacing',
		steps: number[]
	) => {
		const labelId = id( 'l' );
		const max = steps.length - 1;
		const out = h( 'output', { 'aria-live': 'polite' } );
		const mk = ( sign: string, delta: number, verb: string ) => {
			const b = h(
				'button',
				{
					type: 'button',
					class: 'step-btn',
					'aria-label': verb + name,
				},
				sign
			);
			b.addEventListener( 'click', () => {
				const next = current[ key ] + delta;
				if ( next >= 0 && next <= max ) {
					o.onChange( { [ key ]: next } );
				}
			} );
			return b;
		};
		const dec = mk( '−', -1, L.decrease );
		const inc = mk( '+', 1, L.increase );
		syncers.push( ( p ) => {
			out.textContent =
				key === 'fontSize'
					? `${ Math.round( ( FONT_SCALES[ p[ key ] ] ?? 1 ) * 100 ) }%`
					: `${ p[ key ] }/${ max }`;
			dec.setAttribute( 'aria-disabled', String( p[ key ] <= 0 ) );
			inc.setAttribute( 'aria-disabled', String( p[ key ] >= max ) );
		} );
		return h(
			'div',
			{ class: 'row', role: 'group', 'aria-labelledby': labelId },
			h( 'span', { id: labelId, class: 'lbl' }, name ),
			h( 'div', { class: 'step' }, dec, out, inc )
		);
	};

	const choice = < K extends 'contrast' | 'readingGuide' >(
		name: string,
		key: K,
		options: Array< [ Prefs[ K ], string ] >
	) => {
		const labelId = id( 'l' );
		const buttons = options.map( ( [ value, text ] ) => {
			const b = h(
				'button',
				{ type: 'button', class: 'seg-btn', 'aria-pressed': 'false' },
				text
			);
			b.addEventListener( 'click', () =>
				o.onChange( { [ key ]: value } )
			);
			syncers.push( ( p ) =>
				b.setAttribute( 'aria-pressed', String( p[ key ] === value ) )
			);
			return b;
		} );
		return h(
			'div',
			{ class: 'row', role: 'group', 'aria-labelledby': labelId },
			h( 'span', { id: labelId, class: 'lbl' }, name ),
			h( 'div', { class: 'seg' }, ...buttons )
		);
	};

	const section = (
		title: string,
		...items: Array< HTMLElement | null >
	) => {
		const rows = items.filter( ( i ): i is HTMLElement => i !== null );
		if ( ! rows.length ) {
			return null;
		}
		const labelId = id( 's' );
		return h(
			'div',
			{ class: 'sect', role: 'group', 'aria-labelledby': labelId },
			h( 'span', { id: labelId, class: 'sect-t' }, title ),
			...rows
		);
	};
	const when = (
		feature: ( typeof FEATURES )[ number ],
		make: () => HTMLElement
	) => ( on( feature ) ? make() : null );

	const contrastOptions: Array< [ Prefs[ 'contrast' ], string ] > = [
		[ 'off', L.contrastDefault ],
	];
	if ( on( 'contrast_high' ) ) {
		contrastOptions.push( [ 'high', L.contrastHigh ] );
	}
	if ( on( 'contrast_inverted' ) ) {
		contrastOptions.push( [ 'inverted', L.contrastInverted ] );
	}
	if ( on( 'contrast_grayscale' ) ) {
		contrastOptions.push( [ 'grayscale', L.contrastGrayscale ] );
	}

	let ttsToggle: HTMLButtonElement | null = null;
	let ttsHint: HTMLElement | null = null;
	let ttsActions: HTMLElement | null = null;
	const ttsBlock = () => {
		ttsToggle = toggleBtn( L.tts, 'tts', true );
		ttsToggle.addEventListener(
			'click',
			( e ) => {
				if ( ! o.voiceAvailable() ) {
					e.stopImmediatePropagation();
				}
			},
			true
		);
		const hintId = id( 'h' );
		ttsHint = h(
			'p',
			{ id: hintId, class: 'hint', role: 'status' },
			L.ttsUnavailable
		);
		ttsToggle.setAttribute( 'aria-describedby', hintId );
		const read = h(
			'button',
			{ type: 'button', class: 'act', onclick: () => o.onRead() },
			L.readAloud
		);
		const stop = h(
			'button',
			{ type: 'button', class: 'act', onclick: () => o.onStop() },
			L.stopReading
		);
		ttsActions = h( 'div', { class: 'acts' }, read, stop );
		syncers.push( ( p ) => {
			ttsActions!.hidden = ! ( p.tts && o.voiceAvailable() );
		} );
		return h( 'div', { class: 'blk' }, ttsToggle, ttsHint, ttsActions );
	};

	const hintsBlock = () => {
		const list = h( 'ul', { class: 'hints' } );
		const row = ( keys: string, text: string ) =>
			h( 'li', {}, h( 'kbd', { dir: 'ltr' }, keys ), ' ', text );
		list.append(
			row( 'Tab / Shift+Tab', L.hintTab ),
			row( 'Enter / Space', L.hintActivate ),
			row( 'Esc', L.hintEscape )
		);
		if ( cfg.shortcut ) {
			list.append( row( cfg.shortcut, L.hintShortcut ) );
		}
		syncers.push( ( p ) => {
			list.hidden = ! p.keyboardHints;
		} );
		return h(
			'div',
			{ class: 'blk' },
			toggleBtn( L.keyboardHints, 'keyboardHints' ),
			list
		);
	};

	const footer = h(
		'div',
		{ class: 'foot' },
		when( 'reset', () =>
			h(
				'button',
				{
					type: 'button',
					class: 'act reset',
					onclick: () => o.onReset(),
				},
				L.reset
			)
		),
		cfg.statementUrl && on( 'statement_link' )
			? h( 'a', { href: cfg.statementUrl, class: 'lnk' }, L.statement )
			: null,
		cfg.reportUrl && on( 'report_link' )
			? h( 'a', { href: cfg.reportUrl, class: 'lnk' }, L.report )
			: null,
		h( 'p', { class: 'priv' }, L.privacy )
	);

	const body = h(
		'div',
		{ class: 'body' },
		section(
			L.groupText,
			when( 'font_size', () =>
				stepper( L.fontSize, 'fontSize', FONT_SCALES )
			),
			when( 'line_height', () =>
				stepper( L.lineHeight, 'lineHeight', LINE_HEIGHTS )
			),
			when( 'letter_spacing', () =>
				stepper( L.letterSpacing, 'letterSpacing', LETTER_SPACINGS )
			),
			when( 'word_spacing', () =>
				stepper( L.wordSpacing, 'wordSpacing', WORD_SPACINGS )
			),
			when( 'readable_font', () =>
				toggleBtn( L.readableFont, 'readableFont' )
			),
			when( 'dyslexia_font', () =>
				toggleBtn( L.dyslexiaFont, 'dyslexiaFont' )
			)
		),
		section(
			L.groupDisplay,
			contrastOptions.length > 1
				? choice( L.contrast, 'contrast', contrastOptions )
				: null,
			when( 'highlight_links', () =>
				toggleBtn( L.highlightLinks, 'highlightLinks' )
			),
			when( 'highlight_headings', () =>
				toggleBtn( L.highlightHeadings, 'highlightHeadings' )
			),
			when( 'big_cursor', () => toggleBtn( L.bigCursor, 'bigCursor' ) ),
			when( 'stop_animations', () =>
				toggleBtn( L.stopAnimations, 'stopAnimations' )
			),
			when( 'hide_images', () => toggleBtn( L.hideImages, 'hideImages' ) )
		),
		section(
			L.groupReading,
			when( 'reading_guide', () =>
				choice( L.readingGuide, 'readingGuide', [
					[ 'off', L.guideOff ],
					[ 'line', L.guideLine ],
					[ 'mask', L.guideMask ],
				] )
			),
			when( 'text_to_speech', ttsBlock ),
			when( 'keyboard_hints', hintsBlock )
		)
	);

	const titleId = id( 'title' );
	const panelId = id( 'panel' );
	const closeBtn = h(
		'button',
		{ type: 'button', class: 'close', 'aria-label': L.close },
		'×'
	);
	const panel = h(
		'div',
		{
			class: 'panel',
			id: panelId,
			role: 'dialog',
			'aria-modal': 'true',
			'aria-labelledby': titleId,
			dir: isRtl( o.lang ) ? 'rtl' : 'ltr',
			lang: o.lang,
			hidden: true,
		},
		h(
			'div',
			{ class: 'head' },
			h( 'h2', { id: titleId }, L.title ),
			closeBtn
		),
		body,
		footer
	);

	const shortcut = parseShortcut( cfg.shortcut );
	const launcher = h( 'button', {
		type: 'button',
		class: 'launcher',
		'aria-label': L.title,
		'aria-haspopup': 'dialog',
		'aria-expanded': 'false',
		'aria-controls': panelId,
		'aria-keyshortcuts': shortcut
			? cfg.shortcut.replace( /\+/g, '+' )
			: null,
		lang: o.lang,
	} );
	launcher.innerHTML = ICON;

	const guide = h(
		'div',
		{ class: 'guide', 'aria-hidden': 'true' },
		h( 'div', { class: 'g-line' } ),
		h( 'div', { class: 'g-top' } ),
		h( 'div', { class: 'g-bottom' } )
	);
	const root = h(
		'div',
		{ class: 'root', role: 'region', 'aria-label': L.title, lang: o.lang },
		launcher,
		h( 'div', { class: 'anchor' }, panel ),
		guide
	);
	shadow.append( h( 'style', {}, o.css ), root );

	const focusables = (): HTMLElement[] =>
		Array.from(
			panel.querySelectorAll< HTMLElement >( 'button, a[href]' )
		).filter(
			( el ) =>
				! el.hasAttribute( 'disabled' ) &&
				el.getClientRects().length > 0
		);

	const setOpen = ( value: boolean, returnFocus: boolean ) => {
		open = value;
		panel.hidden = ! value;
		launcher.setAttribute( 'aria-expanded', String( value ) );
		if ( value ) {
			refreshVoices();
			( focusables()[ 0 ] ?? closeBtn ).focus();
		} else if ( returnFocus ) {
			launcher.focus();
		}
	};

	launcher.addEventListener( 'click', () => setOpen( ! open, false ) );
	closeBtn.addEventListener( 'click', () => setOpen( false, true ) );

	panel.addEventListener( 'keydown', ( e ) => {
		if ( e.key !== 'Tab' ) {
			return;
		}
		const items = focusables();
		const first = items[ 0 ];
		const last = items[ items.length - 1 ];
		const active = shadow.activeElement;
		if ( ! first || ! last ) {
			return;
		}
		if (
			e.shiftKey &&
			( active === first || ! panel.contains( active ) )
		) {
			e.preventDefault();
			last.focus();
		} else if (
			! e.shiftKey &&
			( active === last || ! panel.contains( active ) )
		) {
			e.preventDefault();
			first.focus();
		}
	} );

	document.addEventListener(
		'pointerdown',
		() => ( pointerDriven = true ),
		true
	);
	document.addEventListener(
		'keydown',
		( e ) => {
			pointerDriven = false;
			if ( e.key === 'Escape' && open ) {
				e.preventDefault();
				setOpen( false, true );
				return;
			}
			if (
				shortcut &&
				e.altKey === shortcut.alt &&
				e.shiftKey === shortcut.shift &&
				e.ctrlKey === shortcut.ctrl &&
				e.metaKey === shortcut.meta &&
				e.code.replace( /^(Key|Digit)/, '' ).toUpperCase() ===
					shortcut.key
			) {
				e.preventDefault();
				setOpen( ! open, true );
			}
		},
		true
	);
	document.addEventListener( 'focusin', ( e ) => {
		if ( open && ! pointerDriven && e.target !== host ) {
			( focusables()[ 0 ] ?? closeBtn ).focus();
		}
	} );

	const refreshVoices = () => {
		const ok = o.voiceAvailable();
		if ( ttsToggle && ttsHint ) {
			ttsToggle.setAttribute( 'aria-disabled', String( ! ok ) );
			ttsHint.hidden = ok;
		}
		if ( current && ttsActions ) {
			ttsActions.hidden = ! ( current.tts && ok );
		}
	};

	let guideBound = false;
	const moveGuide = ( y: number ) =>
		guide.style.setProperty( '--y', `${ Math.round( y ) }px` );
	const onPointer = ( e: PointerEvent ) => moveGuide( e.clientY );
	const onFocusMove = ( e: FocusEvent ) => {
		if ( e.target instanceof HTMLElement && e.target !== host ) {
			const r = e.target.getBoundingClientRect();
			moveGuide( r.top + r.height / 2 );
		}
	};

	return {
		host,
		sync( p ) {
			current = p;
			syncers.forEach( ( fn ) => fn( p ) );
			guide.dataset.mode = p.readingGuide;
			guide.hidden = p.readingGuide === 'off';
			const wantGuide = p.readingGuide !== 'off';
			if ( wantGuide && ! guideBound ) {
				document.addEventListener( 'pointermove', onPointer, {
					passive: true,
				} );
				document.addEventListener( 'focusin', onFocusMove );
				moveGuide( window.innerHeight / 2 );
			} else if ( ! wantGuide && guideBound ) {
				document.removeEventListener( 'pointermove', onPointer );
				document.removeEventListener( 'focusin', onFocusMove );
			}
			guideBound = wantGuide;
			refreshVoices();
		},
		refreshVoices,
		toggle: () => setOpen( ! open, true ),
		isOpen: () => open,
	};
};
